import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'common/components/elements/buttons/primary';
import Modal from 'common/components/modules/modal';
import GenerateColor from 'common/utils/generateTextColor.js';
import Icon from 'common/components/elements/icons';
import TruncateText from 'common/components/elements/truncate-text';
import cx from 'classnames';

import './style.scss';
import QueryParams from 'sites/utils/QueryParams';
import CircleLoader from 'common/components/elements/loader/circle';
import { useSelector } from 'react-redux';
import { isAgeVerificationRequiredSelector } from 'sites/state/modules/common/selectors';
import { filterBadWords } from 'common/utils/filter-bad-words';

const MembershipsForContentModal = ({
   onClose,
   isMobile,
   data,
   onSelectMembership,
   type,
   urlParamForAccess,
   isAuthUser,
   availablePricings,
   isLoading,
}) => {
   const isAgeVerificationRequired = useSelector(isAgeVerificationRequiredSelector)
   const [animate, setAnimate] = useState('up')
   let timer = null;
   useEffect(() => {
      return () => {
         if(Boolean(timer)) {
            clearTimeout(timer)
         }
      }
   }, [timer])
   useEffect(() => {
      QueryParams.setQueryParam('memberships-for-content', `${ type }-${ data.id }`)

      return () => {
         QueryParams.removeQueryParam('memberships-for-content')
      }
      // eslint-disable-next-line
   }, [])

   const closeModal = () => {
      if(isMobile) {
         setAnimate('down')

         timer = setTimeout(() => {
            setAnimate('up')
            onClose()
         }, 800)
      } else {
         onClose()
      }
   }


   const getModalWrapperProps = () => {
      return {
         isMobile: isMobile,
         closeModal: closeModal,
         animate: animate,
         data: data,
         type: type,
         isAgeVerificationRequired,
      }
   }

   if(isLoading) {
      return (
         <ModalWraaper
            { ...getModalWrapperProps() }
         >
            <div className='w-full flex items-center justify-center min-h-[100px]'>
               <CircleLoader color='primary' />
            </div>
         </ModalWraaper>
      );
   }
   const sortPricingsData = () => {
      if(!Boolean(data.is_available_pricings)) {
         return null;
      }
      const res = {
         unlocked: [],
         locked: {},
      }
      availablePricings.forEach(item => {
         if(item.access_type === 'unlocked') {
            res.unlocked = [...res.unlocked, item]
         } else {
            if(Boolean(res.locked[item.access_unlock_price])) {
               res.locked[item.access_unlock_price] = [...res.locked[item.access_unlock_price], item]
            } else {
               res.locked[item.access_unlock_price] = [item]
            }
         }
      })
      return res
   }
   const pricingsData = sortPricingsData()

   if(!pricingsData) {
      return null;
   }
   return (
      <ModalWraaper
         { ...getModalWrapperProps() }
      >
         <ListForPricing
            pricing='unlocked'
            data={ pricingsData.unlocked }
            onSelectMembership={ onSelectMembership }
            //urlParamForAccess={ urlParamForAccess }
            isAuthUser={ isAuthUser }
            isAgeVerificationRequired={ isAgeVerificationRequired }
         />
         {
            Object.keys(pricingsData.locked).map(key => (
               <ListForPricing
                  key={ key }
                  pricing={ key }
                  data={ pricingsData.locked[key] }
                  onSelectMembership={ onSelectMembership }
                  isAuthUser={ isAuthUser }
                  isAgeVerificationRequired={ isAgeVerificationRequired }
                  //urlParamForAccess={ urlParamForAccess }
               />
            ))
         }
      </ModalWraaper>
   )
}

const ModalWraaper = ({ children, isMobile, closeModal, animate, data, type, isAgeVerificationRequired }) => {
   return (
      <Modal
         contentPosition={ isMobile ? 'bottom' : 'center' }
         contentWidth={ isMobile ? '100%' : '375px' }
         onClose={ closeModal }
         isCloseAction={ false }
         animate={ isMobile ? animate : null }
      >
         <div className='flex items-center p-2 h-14'>
            <div className='w-10 h-10'>
               <Button
                  iconName='close'
                  fontIconColor='major'
                  classNames='w-full h-full'
                  padding='none'
                  backgroundColor='transparent'
                  onClick={ closeModal }
                  waveColor='rgba(33, 33, 33, 0.8)'
                  fontIconSize='doubleLarge'
                  waveActive={ false }
               />
            </div>
         </div>
         <div
            className='w-full pb-6'
         >
            <div
               className='flex flex-col items-center px-6 mb-4'
            >
               <div
                  className='flex justify-center items-center w-10 h-10 rounded-full'
                  style={ {
                     backgroundColor: GenerateColor.generateRGBColor(window.skinPrimaryColor, 0.12),
                  } }
               >
                  <Icon
                     name={ type === 'photoset' ? 'content' : type === 'video' ? 'videos' : 'online-stream' }
                     style={ {
                        color: window.skinPrimaryColor,
                     } }
                     size='2xl'
                  />
               </div>
               <TruncateText
                  text={ isAgeVerificationRequired ? filterBadWords(data.title) : data.title }
                  textClass='text-xl leading-[30px] dark:text-major-dark text-major font-semibold'
                  className='mt-3'
               />
            </div>
            <div
               className='w-full px-6 max-h-[487px] overflow-y-auto ams-custom-scrool memberships-container'
            >
               {children}
            </div>
         </div>

      </Modal>
   )
}
const ListForPricing = ({
   pricing,
   data,
   onSelectMembership,
   urlParamForAccess,
   isAuthUser,
   isAgeVerificationRequired,
}) => {
   if(!Boolean(data.length)) {
      return null
   }
   return (
      <div
         className='border border-divider rounded-lg dark:border-divider-dark mt-4 overflow-hidden'
      >
         <div
            className={ cx({
               'w-full flex items-center gap-1 pl-3 pr-2.5 py-2.5': true,
               'bg-warning-12': pricing !== 'unlocked',
               'bg-success-12': pricing === 'unlocked',
            }) }
         >
            <Icon
               name={ pricing === 'unlocked' ? 'unlocked' : 'lock' }
               color={ pricing === 'unlocked' ? 'success' : 'warning' }
               size='xl'
               darkColor={ pricing === 'unlocked' ? 'success-dark' : 'warning-dark' }
            />
            <span className='text-sm leading-4 dark:text-major-dark'>
               {
                  pricing === 'unlocked' ? (
                     'Included with the following memberships'
                  ) : (
                     <>
                        Can be unlocked for &nbsp;
                        <span className='font-medium'>{ pricing } USD with the following membership</span>
                     </>


                  )
               }
            </span>
         </div>
         {
            data.map(item => (
               <div
                  key={ item.id }
                  className='w-full px-4 py-[7px] cursor-pointer hover:bg-hover dark:hover:bg-hover-dark'
                  role='presentation'
                  onClick={ () => onSelectMembership(item, item.is_update) }
               >
                  <div className='flex justify-between gap-2'>
                     <TruncateText
                        text={ isAgeVerificationRequired ? filterBadWords(item.name) : item.name }
                        textClass='leading-5 text-sm font-medium dark:text-major-dark group-hover:underline'
                        width='calc(100% - 30px)'
                        className='w-full group'
                     />
                     <div className='h-5'>
                        <Button
                           text={ item.is_update  ? 'Upgrade' : 'Select' }
                           backgroundColor='transparent'
                           // primaryColor
                           textSize='small'
                           classNames='h-full group'
                           padding='none'
                           textPosition='center'
                           textMarginX='none'
                           waveActive={ false }
                           textColor=' text-major dark:text-major-dark'
                           textClassName='whitespace-nowrap group-hover:underline'
                           lineHeight='20px'
                        />
                     </div>
                  </div>
                  <span className='mt-1 text-xs text-secondary dark:text-secondary-dark '>{ item.usd_terms }</span>
               </div>
            ))
         }
      </div>
   )
}

MembershipsForContentModal.defaultProps = {
   availablePricings: [],
}
MembershipsForContentModal.propTypes = {
   onClose: PropTypes.func,
   isMobile: PropTypes.bool,
   data: PropTypes.object,
   onSelectMembership: PropTypes.func,
   type: PropTypes.string,
   urlParamForAccess: PropTypes.bool,
   isAuthUser: PropTypes.bool,
   availablePricings: PropTypes.array,
   isLoading: PropTypes.bool,
}
ModalWraaper.propTypes = {
   isMobile: PropTypes.bool,
   data: PropTypes.object,
   type: PropTypes.string,
   animate: PropTypes.string,
   closeModal: PropTypes.func,
   children: PropTypes.any,
   isAgeVerificationRequired: PropTypes.bool,
}

ListForPricing.propTypes = {
   pricing: PropTypes.string,
   data: PropTypes.array,
   onSelectMembership: PropTypes.func,
   urlParamForAccess: PropTypes.bool,
   isAuthUser: PropTypes.bool,
   isAgeVerificationRequired: PropTypes.bool,
}

ListForPricing.defaultProps = {
   urlParamForAccess: true,
}

export default MembershipsForContentModal;
